import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  faCheck,
  faPlus,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { isPlainObject } from "lodash-es";
import { twJoin } from "tailwind-merge";

import { DisplayDate, DisplayDateTime } from "src/base-components/Date";
import {
  DateTimePicker,
  DateTimePickerProps,
} from "src/base-components/DateTimePicker";
import { FloatingWindow } from "src/base-components/FloatingWindow/FloatingWindow";
import { Pill } from "src/base-components/Pill";
import { PonEditor } from "src/base-components/PONEditor";
import { CustomPopover } from "src/base-components/Popover";
import {
  CellDisplayType,
  extractCurrencyAmount,
  resolveEffectiveType,
  safeStringify,
} from "src/cases/caseView/blocks/cellTypes";
import { jsonToPon, ponToJson } from "src/datasets/DatasetTable/utils";
import { Icon } from "src/design-system/Icon";
import { useOnClickOutside } from "src/hooks/usehooks-ts";
import { copyTextToClipboard } from "src/utils/clipboard";

type TypedCellEditorProps = {
  value: unknown;
  displayType: CellDisplayType;
  columnKey: string;
  onChange: (newValue: unknown) => void;
  onBlur: () => void;
  columnValues?: unknown[];
};

const TextInputEditor: React.FC<{
  value: unknown;
  onChange: (newValue: unknown) => void;
  onBlur: () => void;
  originalType: "string" | "number" | "currency";
}> = ({ value, onChange, onBlur, originalType }) => {
  const isNumeric = originalType === "number" || originalType === "currency";
  const [localValue, setLocalValue] = useState(() =>
    value === null || value === undefined ? "" : String(value),
  );
  const closingRef = useRef(false);

  const commitValue = () => {
    if (localValue === "") {
      onChange(null);
    } else if (isNumeric) {
      const num = Number(localValue);
      onChange(isNaN(num) ? localValue : num);
    } else {
      onChange(localValue);
    }
  };

  const handleCommit = () => {
    if (closingRef.current) return;
    commitValue();
    onBlur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        closingRef.current = true;
        commitValue();
        return; // propagate to table for row advancement
      case "Escape":
        e.preventDefault();
        closingRef.current = true;
        onBlur();
        break;
      case "ArrowUp":
      case "ArrowDown":
      case "ArrowLeft":
      case "ArrowRight":
      case "Tab":
        return;
    }
    e.stopPropagation();
  };

  return (
    <input
      ref={(node) => node?.focus()}
      className={twJoin(
        "h-full w-full bg-transparent py-1.5 pr-2 pl-1.5 font-inter-normal-12px outline-none",
        isNumeric && "text-right",
      )}
      inputMode={isNumeric ? "decimal" : undefined}
      type="text"
      value={localValue}
      onBlur={handleCommit}
      onChange={(e) => setLocalValue(e.target.value)}
      onKeyDown={handleKeyDown}
    />
  );
};

const BOOLEAN_OPTIONS = [true, false, null] as const;

const BooleanEditor: React.FC<{
  value: unknown;
  onChange: (newValue: unknown) => void;
  onBlur: () => void;
}> = ({ value, onChange, onBlur }) => {
  const hasValue = value !== null && value !== undefined;
  const boolValue = Boolean(value);
  const triggerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(() => {
    if (!hasValue) return 0;
    return boolValue ? 0 : 1;
  });

  const listCallbackRef = useCallback((node: HTMLDivElement | null) => {
    listRef.current = node;
    if (node) requestAnimationFrame(() => node.focus());
  }, []);

  useOnClickOutside([triggerRef, listRef], onBlur);

  const handleSelect = (newValue: boolean | null) => {
    onChange(newValue);
    onBlur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) =>
          Math.min(prev + 1, BOOLEAN_OPTIONS.length - 1),
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        handleSelect(BOOLEAN_OPTIONS[focusedIndex]);
        break;
      case "Escape":
        e.preventDefault();
        onBlur();
        break;
      case "Tab":
        // Let Tab propagate to table for navigation
        return;
    }
    e.stopPropagation();
  };

  return (
    <div ref={triggerRef} className="h-full w-full min-w-0">
      <CustomPopover
        button={
          <div className="flex h-full w-full items-center py-1.5 pr-2 pl-1.5">
            {hasValue && (
              <Pill size="sm" variant={boolValue ? "green" : "red"}>
                <Pill.Text>{boolValue ? "True" : "False"}</Pill.Text>
              </Pill>
            )}
          </div>
        }
        className="w-44"
        isOpen={true}
        offsetX={8}
        placement="bottom-start"
        wrapperClassName="w-full"
      >
        <div
          ref={listCallbackRef}
          className="outline-none"
          role="listbox"
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          {([true, false] as const).map((bool, index) => (
            <button
              key={String(bool)}
              aria-selected={hasValue && boolValue === bool}
              className={twJoin(
                "flex w-full items-center justify-between px-4 py-2.5 font-inter-normal-13px text-gray-800",
                focusedIndex === index ? "bg-gray-50" : "hover:bg-gray-50",
              )}
              role="option"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(bool);
              }}
              onMouseEnter={() => setFocusedIndex(index)}
            >
              <Pill size="sm" variant={bool ? "green" : "red"}>
                <Pill.Text>{bool ? "True" : "False"}</Pill.Text>
              </Pill>
              {hasValue && boolValue === bool && (
                <Icon icon={faCheck} size="xs" variant="active" />
              )}
            </button>
          ))}
          <div className="my-1 border-gray-100 border-t" />
          <button
            className={twJoin(
              "flex w-full items-center gap-2 px-4 py-2.5 font-inter-normal-13px text-gray-800",
              focusedIndex === 2 ? "bg-gray-50" : "hover:bg-gray-50",
            )}
            role="option"
            onMouseDown={(e) => {
              e.preventDefault();
              handleSelect(null);
            }}
            onMouseEnter={() => setFocusedIndex(2)}
          >
            <Icon icon={faTrashCan} size="xs" />
            Remove selection
          </button>
        </div>
      </CustomPopover>
    </div>
  );
};

const parseDate = (v: unknown): Date | undefined => {
  if (!v || typeof v !== "string") return undefined;
  const parsed = new Date(v);
  return isNaN(parsed.getTime()) ? undefined : parsed;
};

const DateEditor: React.FC<{
  value: unknown;
  dateOnly: boolean;
  onChange: (newValue: unknown) => void;
  onBlur: () => void;
}> = ({ value, dateOnly, onChange, onBlur }) => {
  const [originalValue] = useState(value);
  const originalDate = useMemo(() => parseDate(originalValue), [originalValue]);
  const dateValue = useMemo(() => parseDate(value), [value]);
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useOnClickOutside([triggerRef, panelRef], (e) => {
    if ((e.target as Element)?.closest?.("[data-headlessui-portal]")) return;
    onBlur();
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onBlur();
    }
  };

  const handleChange: DateTimePickerProps["onChange"] = (date) => {
    if (!date) {
      onChange(null);
      return;
    }
    if (originalDate && date.getTime() === originalDate.getTime()) {
      onChange(originalValue);
      return;
    }
    onChange(date.toISOString());
  };

  return (
    <div ref={triggerRef} className="h-full w-full" onKeyDown={handleKeyDown}>
      <CustomPopover
        button={
          <div className="flex h-full w-full items-center py-1.5 pr-2 pl-1.5">
            {dateValue ? (
              dateOnly ? (
                <DisplayDate value={dateValue.toISOString()} />
              ) : (
                <DisplayDateTime value={dateValue.toISOString()} />
              )
            ) : (
              "Pick a date"
            )}
          </div>
        }
        isOpen={true}
        offsetX={8}
        placement="bottom-start"
        wrapperClassName="w-full"
      >
        <div ref={panelRef}>
          <DateTimePicker
            dateOnly={dateOnly}
            value={dateValue}
            onChange={handleChange}
          />
        </div>
      </CustomPopover>
    </div>
  );
};

const EnumEditor: React.FC<{
  value: unknown;
  columnValues?: unknown[];
  onChange: (newValue: unknown) => void;
  onBlur: () => void;
}> = ({ value, columnValues, onChange, onBlur }) => {
  const [isAddingValue, setIsAddingValue] = useState(false);
  const [search, setSearch] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const options = useMemo(
    () =>
      Array.from(
        new Set([
          ...(columnValues ?? [])
            .filter((v) => v !== null && v !== undefined)
            .map((v) => String(v)),
          ...(value !== null && value !== undefined ? [String(value)] : []),
        ]),
      ),
    [columnValues, value],
  );

  const [rawFocusedIndex, setRawFocusedIndex] = useState(() => {
    const idx = options.findIndex((o) => String(value) === o);
    return idx >= 0 ? idx : 0;
  });

  const filteredOptions = useMemo(() => {
    if (!isAddingValue || !search) return options;
    const lower = search.toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(lower));
  }, [options, search, isAddingValue]);

  // Clamp to valid range so a stale index never renders or is used by handlers
  const focusedIndex = Math.min(rawFocusedIndex, filteredOptions.length);

  // Auto-focus the list when not in add mode
  useEffect(() => {
    if (!isAddingValue) {
      requestAnimationFrame(() => listRef.current?.focus());
    }
  }, [isAddingValue]);

  useOnClickOutside([triggerRef, listRef], onBlur);

  const scrollFocusedIntoView = (index: number) => {
    const items = listRef.current?.querySelectorAll("[role='option']");
    items?.[index]?.scrollIntoView({ block: "nearest" });
  };

  const totalItems = filteredOptions.length + 1; // +1 for "Add value"

  const handleSelectOption = (opt: string) => {
    const isCurrentlySelected =
      value !== null && value !== undefined && String(value) === opt;
    onChange(isCurrentlySelected ? null : opt);
    onBlur();
  };

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const next = Math.min(focusedIndex + 1, totalItems - 1);
        setRawFocusedIndex(next);
        scrollFocusedIntoView(next);
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prev = Math.max(focusedIndex - 1, 0);
        setRawFocusedIndex(prev);
        scrollFocusedIntoView(prev);
        break;
      }
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[focusedIndex]);
        } else if (focusedIndex === filteredOptions.length) {
          setIsAddingValue(true);
        }
        break;
      case "Escape":
        e.preventDefault();
        onBlur();
        break;
      case "Tab":
        return;
    }
    e.stopPropagation();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "Escape":
        e.preventDefault();
        setIsAddingValue(false);
        setSearch("");
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[focusedIndex]);
        } else if (search) {
          handleSelectOption(search);
        } else if (filteredOptions.length > 0) {
          handleSelectOption(filteredOptions[0]);
        }
        break;
      case "ArrowDown": {
        e.preventDefault();
        const nextItems = search ? filteredOptions.length + 1 : totalItems;
        const next = Math.min(focusedIndex + 1, nextItems - 1);
        setRawFocusedIndex(next);
        scrollFocusedIntoView(next);
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prev = Math.max(focusedIndex - 1, 0);
        setRawFocusedIndex(prev);
        scrollFocusedIntoView(prev);
        break;
      }
      case "Tab":
        return;
    }
    e.stopPropagation();
  };

  return (
    <div ref={triggerRef} className="h-full w-full min-w-0">
      <CustomPopover
        button={
          isAddingValue ? (
            <div className="flex h-full w-full items-center py-1.5 pr-2 pl-1.5">
              <input
                ref={(node) => node?.focus()}
                aria-autocomplete="list"
                aria-expanded={true}
                className="h-full w-full bg-transparent font-inter-normal-12px outline-none"
                placeholder="Type new value..."
                role="combobox"
                value={search}
                onBlur={onBlur}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleInputKeyDown}
              />
            </div>
          ) : (
            <div className="flex h-full w-full items-center py-1.5 pr-2 pl-1.5">
              {value !== null && value !== undefined && (
                <Pill size="sm" variant="gray">
                  <Pill.Text>{String(value)}</Pill.Text>
                </Pill>
              )}
            </div>
          )
        }
        className="decideScrollbar max-h-48 w-48 overflow-auto"
        isOpen={true}
        offsetX={8}
        placement="bottom-start"
        wrapperClassName="w-full"
      >
        <div
          ref={listRef}
          className="outline-none"
          role="listbox"
          tabIndex={isAddingValue ? undefined : 0}
          onKeyDown={!isAddingValue ? handleListKeyDown : undefined}
        >
          {filteredOptions.map((opt, index) => {
            const isSelected = String(value) === opt;
            return (
              <button
                key={opt}
                aria-selected={isSelected}
                className={twJoin(
                  "flex w-full items-center justify-between px-4 py-2.5 text-left",
                  index === focusedIndex ? "bg-gray-50" : "hover:bg-gray-50",
                )}
                role="option"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelectOption(opt);
                }}
                onMouseEnter={() => setRawFocusedIndex(index)}
              >
                <Pill size="sm" variant="gray">
                  <Pill.Text>{opt}</Pill.Text>
                </Pill>
                {isSelected && (
                  <Icon icon={faCheck} size="xs" variant="active" />
                )}
              </button>
            );
          })}
          {filteredOptions.length === 0 && !search && (
            <div className="px-4 py-2.5 font-inter-normal-13px text-gray-400">
              No options
            </div>
          )}
          {filteredOptions.length > 0 && (
            <div className="my-1 border-gray-100 border-t" />
          )}
          {isAddingValue && search ? (
            <button
              className={twJoin(
                "flex w-full items-center gap-1.5 px-4 py-2.5 text-left font-inter-normal-13px text-gray-800",
                focusedIndex === filteredOptions.length
                  ? "bg-gray-50"
                  : "hover:bg-gray-50",
              )}
              role="option"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectOption(search);
              }}
            >
              <Icon icon={faPlus} size="2xs" />
              <span>Add</span>
              <Pill size="sm" variant="gray">
                <Pill.Text>{search}</Pill.Text>
              </Pill>
            </button>
          ) : (
            <button
              className={twJoin(
                "flex w-full items-center gap-1.5 px-4 py-2.5 text-left font-inter-normal-13px text-gray-800",
                !isAddingValue && focusedIndex === filteredOptions.length
                  ? "bg-gray-50"
                  : "hover:bg-gray-50",
              )}
              role="option"
              onMouseDown={(e) => {
                e.preventDefault();
                setIsAddingValue(true);
              }}
              onMouseEnter={() => setRawFocusedIndex(filteredOptions.length)}
            >
              <Icon icon={faPlus} size="2xs" />
              <span>Add value</span>
            </button>
          )}
        </div>
      </CustomPopover>
    </div>
  );
};

const TagsEditor: React.FC<{
  value: unknown;
  columnValues?: unknown[];
  onChange: (newValue: unknown) => void;
  onBlur: () => void;
}> = ({ value, columnValues, onChange, onBlur }) => {
  const [isAddingValue, setIsAddingValue] = useState(false);
  const [search, setSearch] = useState("");
  const [rawFocusedIndex, setRawFocusedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const currentValue = useMemo(() => {
    if (Array.isArray(value)) return value.map(String);
    if (value !== null && value !== undefined) return [String(value)];
    return [];
  }, [value]);

  const options = useMemo(
    () =>
      Array.from(
        new Set([
          ...(columnValues ?? [])
            .flatMap((v) => (Array.isArray(v) ? v : [v]))
            .filter((v) => v !== null && v !== undefined)
            .map((v) => String(v)),
          ...currentValue,
        ]),
      ),
    [columnValues, currentValue],
  );

  const filteredOptions = useMemo(() => {
    if (!isAddingValue || !search) return options;
    const lower = search.toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(lower));
  }, [options, search, isAddingValue]);

  // Clamp to valid range so a stale index never renders or is used by handlers
  const focusedIndex = Math.min(rawFocusedIndex, filteredOptions.length);

  // Auto-focus the list when not in add mode
  useEffect(() => {
    if (!isAddingValue) {
      requestAnimationFrame(() => listRef.current?.focus());
    }
  }, [isAddingValue]);

  useOnClickOutside([triggerRef, listRef], onBlur);

  const scrollFocusedIntoView = (index: number) => {
    const items = listRef.current?.querySelectorAll("[role='option']");
    items?.[index]?.scrollIntoView({ block: "nearest" });
  };

  const totalItems = filteredOptions.length + 1; // +1 for "Add value"

  const handleToggleTag = (tag: string) => {
    const newValue = currentValue.includes(tag)
      ? currentValue.filter((v) => v !== tag)
      : [...currentValue, tag];
    onChange(newValue);
  };

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const next = Math.min(focusedIndex + 1, totalItems - 1);
        setRawFocusedIndex(next);
        scrollFocusedIntoView(next);
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prev = Math.max(focusedIndex - 1, 0);
        setRawFocusedIndex(prev);
        scrollFocusedIntoView(prev);
        break;
      }
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
          handleToggleTag(filteredOptions[focusedIndex]);
        } else if (focusedIndex === filteredOptions.length) {
          setIsAddingValue(true);
        }
        break;
      case "Escape":
        e.preventDefault();
        onBlur();
        break;
      case "Tab":
        return;
    }
    e.stopPropagation();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "Escape":
        e.preventDefault();
        setIsAddingValue(false);
        setSearch("");
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
          handleToggleTag(filteredOptions[focusedIndex]);
          setSearch("");
        } else if (search) {
          if (!currentValue.includes(search)) {
            onChange([...currentValue, search]);
          }
          setSearch("");
          setIsAddingValue(false);
        } else if (filteredOptions.length > 0) {
          handleToggleTag(filteredOptions[0]);
          setSearch("");
        }
        break;
      case "ArrowDown": {
        e.preventDefault();
        const nextItems = search ? filteredOptions.length + 1 : totalItems;
        const next = Math.min(focusedIndex + 1, nextItems - 1);
        setRawFocusedIndex(next);
        scrollFocusedIntoView(next);
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prev = Math.max(focusedIndex - 1, 0);
        setRawFocusedIndex(prev);
        scrollFocusedIntoView(prev);
        break;
      }
      case "Tab":
        return;
    }
    e.stopPropagation();
  };

  return (
    <div ref={triggerRef} className="h-full w-full">
      <CustomPopover
        button={
          isAddingValue ? (
            <div className="flex h-full w-full min-w-0 items-center gap-1 py-1.5 pr-2 pl-1.5">
              <input
                ref={(node) => node?.focus()}
                aria-autocomplete="list"
                aria-expanded={true}
                aria-multiselectable={true}
                className="h-full w-full bg-transparent font-inter-normal-12px outline-none"
                placeholder="Type new value..."
                role="combobox"
                value={search}
                onBlur={onBlur}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleInputKeyDown}
              />
            </div>
          ) : (
            <div className="flex h-full w-full min-w-0 items-center gap-1 py-1.5 pr-2 pl-1.5">
              {currentValue.length > 0 && (
                <>
                  <Pill size="sm" variant="gray">
                    <Pill.Text>{currentValue[0]}</Pill.Text>
                  </Pill>
                  {currentValue.length > 1 && (
                    <Pill size="sm" variant="gray">
                      <Pill.Text>+{currentValue.length - 1}</Pill.Text>
                    </Pill>
                  )}
                </>
              )}
            </div>
          )
        }
        className="decideScrollbar max-h-48 w-48 overflow-auto"
        isOpen={true}
        offsetX={8}
        placement="bottom-start"
        wrapperClassName="w-full"
      >
        <div
          ref={listRef}
          className="outline-none"
          role="listbox"
          tabIndex={isAddingValue ? undefined : 0}
          onKeyDown={!isAddingValue ? handleListKeyDown : undefined}
        >
          {filteredOptions.map((opt, index) => {
            const isSelected = currentValue.includes(opt);
            return (
              <button
                key={opt}
                aria-selected={isSelected}
                className={twJoin(
                  "flex w-full items-center justify-between px-4 py-2.5 text-left",
                  index === focusedIndex ? "bg-gray-50" : "hover:bg-gray-50",
                )}
                role="option"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleToggleTag(opt);
                }}
                onMouseEnter={() => setRawFocusedIndex(index)}
              >
                <Pill size="sm" variant="gray">
                  <Pill.Text>{opt}</Pill.Text>
                </Pill>
                {isSelected && (
                  <Icon icon={faCheck} size="xs" variant="active" />
                )}
              </button>
            );
          })}
          {filteredOptions.length === 0 && !search && (
            <div className="px-4 py-2.5 font-inter-normal-13px text-gray-400">
              No tags
            </div>
          )}
          {filteredOptions.length > 0 && (
            <div className="my-1 border-gray-100 border-t" />
          )}
          {isAddingValue && search ? (
            <button
              className={twJoin(
                "flex w-full items-center gap-1.5 px-4 py-2.5 text-left font-inter-normal-13px",
                currentValue.includes(search)
                  ? "cursor-not-allowed opacity-50"
                  : focusedIndex === filteredOptions.length
                    ? "bg-gray-50 text-gray-800"
                    : "text-gray-800 hover:bg-gray-50",
              )}
              role="option"
              onMouseDown={(e) => {
                e.preventDefault();
                if (currentValue.includes(search)) return;
                onChange([...currentValue, search]);
                setSearch("");
                setIsAddingValue(false);
              }}
            >
              <Icon icon={faPlus} size="2xs" />
              <span>Add</span>
              <Pill size="sm" variant="gray">
                <Pill.Text>{search}</Pill.Text>
              </Pill>
            </button>
          ) : (
            <button
              className={twJoin(
                "flex w-full items-center gap-1.5 px-4 py-2.5 text-left font-inter-normal-13px text-gray-800",
                !isAddingValue && focusedIndex === filteredOptions.length
                  ? "bg-gray-50"
                  : "hover:bg-gray-50",
              )}
              role="option"
              onMouseDown={(e) => {
                e.preventDefault();
                setIsAddingValue(true);
              }}
              onMouseEnter={() => setRawFocusedIndex(filteredOptions.length)}
            >
              <Icon icon={faPlus} size="2xs" />
              <span>Add value</span>
            </button>
          )}
        </div>
      </CustomPopover>
    </div>
  );
};

const JsonEditor: React.FC<{
  value: unknown;
  columnKey: string;
  onChange: (newValue: unknown) => void;
  onBlur: () => void;
}> = ({ value, columnKey, onChange, onBlur }) => {
  const compactValue = useMemo(() => safeStringify(value), [value]);
  const triggerRef = useRef<HTMLDivElement>(null);

  const ponValue = useMemo(() => {
    try {
      return jsonToPon(JSON.stringify(value, null, 2));
    } catch {
      return jsonToPon("{}");
    }
  }, [value]);

  const pendingValueRef = useRef<string | null>(null);

  const handleChange = (newPonValue: string) => {
    try {
      const jsonStr = ponToJson(newPonValue);
      JSON.parse(jsonStr);
      pendingValueRef.current = jsonStr;
    } catch {
      // noop
    }
  };

  const commitAndClose = () => {
    if (
      pendingValueRef.current !== null &&
      pendingValueRef.current !== compactValue
    ) {
      onChange(JSON.parse(pendingValueRef.current));
    }
    onBlur();
  };

  const flushAndClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    commitAndClose();
  };

  const handleCopy = () => {
    if (pendingValueRef.current !== null) {
      try {
        const formatted = JSON.stringify(
          JSON.parse(pendingValueRef.current),
          null,
          2,
        );
        copyTextToClipboard(formatted);
        return;
      } catch {
        // noop
      }
    }
    copyTextToClipboard(JSON.stringify(value, null, 2));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === "Escape") {
      e.preventDefault();
      flushAndClose();
    }
  };

  const dataLoc = `json-editor-${columnKey}`;

  useOnClickOutside(triggerRef, (e) => {
    if ((e.target as Element).closest?.(`[data-loc="${dataLoc}"]`)) return;
    flushAndClose();
  });

  return (
    <div
      ref={triggerRef}
      className="h-full w-full min-w-0"
      onKeyDown={handleKeyDown}
    >
      <FloatingWindow
        button={
          <div className="flex h-full w-full items-center py-1.5 pr-2 pl-1.5">
            <Pill fontType="code-12" size="sm" variant="gray" maxWidth>
              <Pill.Text>{compactValue.slice(0, 75)}</Pill.Text>
            </Pill>
          </div>
        }
        dataLoc={dataLoc}
        draggable={true}
        height="h-80"
        isOpen={true}
        isTrackingReference={true}
        maximizable={true}
        offset={[0, 8]}
        pinnable={false}
        placement="bottom start"
        title={columnKey}
        width="w-120"
        onClose={flushAndClose}
        onCopy={handleCopy}
      >
        <PonEditor value={ponValue} onChange={handleChange} />
      </FloatingWindow>
    </div>
  );
};

export const TypedCellEditor: React.FC<TypedCellEditorProps> = ({
  value,
  displayType,
  columnKey,
  onChange,
  onBlur,
  columnValues,
}) => {
  const effectiveType = resolveEffectiveType(value, displayType);

  switch (effectiveType.type) {
    case "string":
    case "number":
      return (
        <TextInputEditor
          originalType={effectiveType.type}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
        />
      );

    case "currency": {
      const handleCurrencyUpdate = (newValue: unknown) => {
        if (newValue === null) {
          onChange(null);
        } else if (isPlainObject(value)) {
          onChange({ ...(value as Record<string, unknown>), amount: newValue });
        } else {
          onChange(newValue);
        }
      };
      return (
        <TextInputEditor
          originalType="currency"
          value={extractCurrencyAmount(value)}
          onBlur={onBlur}
          onChange={handleCurrencyUpdate}
        />
      );
    }

    case "boolean":
      return (
        <BooleanEditor value={value} onBlur={onBlur} onChange={onChange} />
      );

    case "date":
    case "datetime":
      return (
        <DateEditor
          dateOnly={effectiveType.type === "date"}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
        />
      );

    case "enum":
      return (
        <EnumEditor
          columnValues={columnValues}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
        />
      );

    case "tags":
      return (
        <TagsEditor
          columnValues={columnValues}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
        />
      );

    case "list":
    case "object":
      return (
        <JsonEditor
          columnKey={columnKey}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
        />
      );
  }
};
